/**
 * SearchBar.css — Host-page search bar (features/optional/search-bar.js).
 *
 * ⚠ SHARED/BASE — structure for EVERY winery that turns the search bar on.
 * Injected into the search bar's OWN shadow root (not the widget panel's), so
 * `:host` here is the container spliced into the winery's page, and nothing on
 * their site can restyle these rules.
 *
 * THEMING — never edit this file for one winery:
 *   - colours/size come from the --ws-search-* vars below, which fall back to
 *     the widget's normal --ws-* theme vars (core/theme.js);
 *   - placement is site-specific by nature, so each winery positions the bar
 *     from `variants/<slug>/styles.css` via `:host(.ws-tenant-<slug>)` — that
 *     class is on this host too (search-bar.js), exactly like the panel root.
 *
 * Ported from the old fork's index.css .search-container block (lines 247-380).
 */

/* A [hidden] element must actually hide: several blocks below set `display`,
   which otherwise wins over the UA's [hidden] rule. */
[hidden] {
  display: none !important;
}

/* Sizing lives on the HOST, not on .ws-search: a winery may mount the bar into
   normal flow or (like Goosecross) position it absolutely, and an absolutely
   positioned box with auto width shrink-to-fits — a `width: 100%` child would
   then resolve against its own content instead of the intended width. */
:host {
  display: block;
  width: var(--ws-search-width);
  max-width: 100%;
  /* Hiding below the winery's mobile breakpoint is done in JS, from the
     `search_bar.min_width` config key — a media query here could not read it. */
  --ws-search-width: 600px;
  --ws-search-radius: 20px;
  --ws-search-bg: #ffffff;
  --ws-search-border: #000000;
  --ws-search-text: #1d1d1d;
  --ws-search-muted: #737373;
  --ws-search-hover: #f9fafb;
  --ws-search-divider: #e5e5e5;
  /* Send arrow — the one obviously brand-coloured part. Falls back to the
     winery's accent so a new tenant is themed without setting anything. */
  --ws-search-send: var(--ws-accent, #8c8c8c);
}

/* The card is ONLY the input row; the dropdown is anchored beneath it (see
   .ws-search-panel) rather than growing this box. The old widget did grow one
   box downward, which meant its height changed between the closed and open
   state — anything positioning the bar (Goosecross centres it on the nav line)
   then moved as the dropdown opened. Keeping the row a fixed height makes the
   placement stable, and the panel's shared border/radius keeps the one-card look. */
.ws-search {
  position: relative;
  box-sizing: border-box;
  width: 100%;
  font-family: var(--ws-font, "Open Sans", sans-serif);
  background: var(--ws-search-bg);
  border: 1px solid var(--ws-search-border);
  border-radius: var(--ws-search-radius);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.05);
  z-index: 99;
}

/* Open: square off the bottom so the row and the panel read as one shape. */
.ws-search.is-open {
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
}

.ws-search-panel {
  position: absolute;
  top: 100%;
  /* -1px so the panel's border sits exactly on the card's, not beside it. */
  left: -1px;
  right: -1px;
  background: var(--ws-search-bg);
  border: 1px solid var(--ws-search-border);
  border-top: none;
  border-radius: 0 0 var(--ws-search-radius) var(--ws-search-radius);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.05);
  overflow: hidden;
}

/* ── Input row ─────────────────────────────────────────── */

.ws-search-bar {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px;
}

/* Open state — hairline under the input, separating it from the panel. */
.ws-search.is-open .ws-search-bar {
  border-bottom: 2px solid var(--ws-search-divider);
}

.ws-search-icons {
  position: relative;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  color: var(--ws-search-text);
}

/* The little sparkle overlaps the magnifier's top-left, as in the original. */
.ws-search-sparkle {
  position: absolute;
  top: -6px;
  left: -5px;
  display: inline-flex;
  pointer-events: none;
}

.ws-search-input {
  flex: 1;
  min-width: 0;
  border: none;
  outline: none;
  background: transparent;
  font-family: inherit;
  font-size: 14px;
  font-weight: 400;
  color: var(--ws-search-text);
}

.ws-search-input::placeholder {
  color: var(--ws-search-muted);
  font-weight: 400;
  opacity: 1;
}

.ws-search-clear,
.ws-search-send {
  flex-shrink: 0;
  background: transparent;
  border: none;
  cursor: pointer;
  display: grid;
  place-items: center;
  padding: 2px;
  color: var(--ws-search-muted);
}

.ws-search-send {
  color: var(--ws-search-send);
}

.ws-search-clear[hidden] {
  display: none;
}

/* Disabled while a request is in flight (mirrors the panel's is-disabled). */
.ws-search-send:disabled,
.ws-search-input:disabled {
  cursor: default;
  opacity: 0.5;
}

/* ── Suggestions dropdown ──────────────────────────────── */

.ws-search-suggestions {
  max-height: 350px;
  overflow-y: auto;
  padding-inline: 16px;
  padding-bottom: 8px;
  text-align: left;
}

.ws-search-category {
  padding-block: 8px;
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--ws-search-muted);
}

.ws-search-item {
  display: flex;
  width: 100%;
  box-sizing: border-box;
  align-items: center;
  gap: 8px;
  padding: 12px 8px;
  background: transparent;
  border: none;
  border-radius: 12px;
  font-family: inherit;
  font-size: 14px;
  color: var(--ws-search-text);
  text-align: left;
  cursor: pointer;
  transition: background 0.15s;
}

.ws-search-item:hover,
.ws-search-item:focus-visible {
  background: var(--ws-search-hover);
  outline: none;
}

.ws-search-item-icon {
  flex-shrink: 0;
  display: inline-flex;
  color: var(--ws-search-muted);
}

.ws-search-divider {
  border-bottom: 1px solid var(--ws-search-divider);
}

/* ── In-place loading state ────────────────────────────── */
/* The old widget replaced the dropdown with a 350px block holding the grape-dot
   animation over "Generating response for you...", and only opened the chat
   panel once the answer had landed. Same asset, same geometry (old index.css
   347-368: 350px block, 180px-tall animation, 14px italic caption). */

.ws-search-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  box-sizing: border-box;
  height: 350px;
  padding: 16px;
}

.ws-search-loading-img {
  width: 100%;
  height: 180px;
  object-fit: contain;
}

.ws-search-loading-text {
  margin: 0;
  font-size: 14px;
  font-style: italic;
  text-align: center;
  color: var(--ws-search-text);
}
